import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DatePicker from 'react-datepicker';
import { fr, enGB, nl } from 'date-fns/locale';
import getRevenueSummary from '../../../utility/getRevenueSummary';
import { compareSummaries, periodBounds, periodDays, previousPeriod, validPeriod, todayRange } from '../../../utility/posRevenueComparison';
import { exportPosComparison } from '../../../utility/exportPosComparison';
import ComparisonResults, { PeriodLegend, OrganizationComparison } from './ComparisonResults';
import { Icons } from '../../../images/icons';
import './RevenueComparison.scss';

export default function RevenueComparison({ enabled, organizations, orgId, startDate, endDate, setDateRange, paymentMethod, resetToken, configuration: externalConfiguration, setConfiguration: externalSetConfiguration }) {
  const { t, i18n } = useTranslation('dashboard');
  const [localConfiguration, setLocalConfiguration] = useState({ secondPeriod: null, applied: null, opened: false });
  const configuration = externalConfiguration || localConfiguration;
  const setConfiguration = externalSetConfiguration || setLocalConfiguration;
  const { secondPeriod, applied } = configuration;
  const setSecondPeriod = useCallback((value) => setConfiguration((previous) => ({ ...previous, secondPeriod: value })), [setConfiguration]);
  const setApplied = useCallback((value) => setConfiguration((previous) => ({ ...previous, applied: value })), [setConfiguration]);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const opened = useRef(configuration.opened);
  const previousReset = useRef(resetToken);
  const requestNumber = useRef(0);
  const first = [startDate, endDate];
  const second = secondPeriod || previousPeriod(first);
  const drafts = useRef(null);
  drafts.current = { first, second };
  const selectedIds = useMemo(() => [...new Set(orgId || [])].sort(), [orgId]);
  const orgKey = selectedIds.join(',');
  const selectedOrganizations = organizations.filter((org) => selectedIds.includes(org.id));
  const queryKey = JSON.stringify([orgKey, paymentMethod || 'all', applied]);
  const visibleResults = results?.key === queryKey ? results : null;
  const dateLocale = { fr, en: enGB, nl }[i18n.language.split('-')[0]] || enGB;
  const formatDate = (date) => new Date(date).toLocaleDateString(i18n.language);

  useEffect(() => {
    if (!enabled) return;
    const { first: draftFirst, second: draftSecond } = drafts.current;
    const range = validPeriod(draftFirst) ? draftFirst : todayRange();
    const other = opened.current ? draftSecond : previousPeriod(range);
    opened.current = true;
    setConfiguration((previous) => ({ ...previous, opened: true }));
    setSecondPeriod(other);
    setApplied(validPeriod(draftFirst) && validPeriod(other) ? { period1: range, period2: other } : null);
    setResults(null);
  }, [enabled, setConfiguration, setSecondPeriod, setApplied]);

  useEffect(() => {
    if (previousReset.current === resetToken) return;
    previousReset.current = resetToken;
    const range = todayRange(), other = previousPeriod(range);
    setSecondPeriod(other);
    if (enabled) setApplied({ period1: range, period2: other });
  }, [resetToken, enabled, setSecondPeriod, setApplied]);

  useEffect(() => {
    const number = ++requestNumber.current;
    setError(false);
    const [idsKey, method, periods] = JSON.parse(queryKey);
    const ids = idsKey.split(',').filter(Boolean);
    if (!enabled || !periods || !ids.length) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    setLoading(true);
    setResults(null);
    setError(false);
    const requested = { period1: periods.period1.map((date) => new Date(date)), period2: periods.period2.map((date) => new Date(date)) };
    Promise.all([requested.period1, requested.period2].map((period) => {
      const [start, end] = periodBounds(period);
      return getRevenueSummary(ids, start, end, { paymentMethod: method, includeOrganizations: true, signal: controller.signal });
    })).then(([a, b]) => {
      if (requestNumber.current === number && !controller.signal.aborted) {
        if (!a?.byOrganization || !b?.byOrganization) throw new Error('Comparison API unavailable');
        setResults({ a, b, ...requested, key: queryKey });
      }
    }).catch((failure) => {
      if (requestNumber.current === number && !controller.signal.aborted) setError(true);
    }).finally(() => {
      if (requestNumber.current === number && !controller.signal.aborted) setLoading(false);
    });
    return () => controller.abort();
  }, [enabled, queryKey, retry]);

  if (!enabled) return null;
  return <div className="pos-revenue-comparison">
    <section className="themeCard pos-comparison-controls">
      <div className="pos-comparison-heading"><span className="pos-comparison-heading-icon"><img src={Icons.pos} alt="" /></span><div><h2>{t('comparison_title')}</h2><p>{t('comparison_intro')}</p></div></div>
      <div className="pos-comparison-periods">
        {[[first, setDateRange, 'pos-period-1', 'comparison_p1'], [second, setSecondPeriod, 'pos-period-2', 'comparison_p2']].map(([period, setPeriod, id, label]) =>
          <div key={id}><label htmlFor={id}>{t(label)}</label><DatePicker id={id} selectsRange startDate={period[0]} endDate={period[1]} onChange={setPeriod} dateFormat="dd/MM/yyyy" locale={dateLocale} placeholderText={t('comparison_choose_dates')} /></div>)}
        <button className="themeBtn pos-comparison-apply" disabled={!validPeriod(first) || !validPeriod(second) || !selectedIds.length || loading}
          onClick={() => {
            setApplied({ period1: first.map((date) => new Date(date)), period2: second.map((date) => new Date(date)) });
            setRetry((value) => value + 1);
          }}>{t('comparison_apply')}</button>
      </div>
      <details className="pos-comparison-help"><summary>{t('comparison_reading_help')}</summary><p>{t('comparison_mixed_note')}</p></details>
    </section>
    {!selectedIds.length && <p role="status">{t('comparison_choose_orgs')}</p>}
    {loading && <p role="status" aria-live="polite">{t('comparison_loading')}</p>}
    {error && <div role="alert"><p>{t("comparison_load_failed")}</p><button className="themeBtn themeBtnSecondary" onClick={() => setRetry((value) => value + 1)}>{t('comparison_retry')}</button></div>}
    {visibleResults && <>
      <div className="pos-comparison-summary"><div><PeriodLegend /><p>{t('comparison_p1')}: {visibleResults.period1.map(formatDate).join(' — ')}<br />
        {t('comparison_p2')}: {visibleResults.period2.map(formatDate).join(' — ')}
        {periodDays(visibleResults.period1) !== periodDays(visibleResults.period2) && <><br />{t('comparison_duration', { first: periodDays(visibleResults.period1), second: periodDays(visibleResults.period2) })}</>}</p></div>
        <button className="themeBtn themeBtnSecondary" onClick={() => exportPosComparison({ results: visibleResults, organizations: selectedOrganizations, paymentMethod, t, formatDate })}>{t('comparison_export')}</button>
      </div>
      <h2 className="pos-comparison-results-title">{t('comparison_global')}</h2>
      <ComparisonResults model={compareSummaries(visibleResults.a, visibleResults.b, paymentMethod)} />
      <section className="pos-comparison-organizations"><h2>{t('comparison_by_org')}</h2>
        {selectedOrganizations.map((org) => <OrganizationComparison key={org.id} organization={org}
          model={compareSummaries(visibleResults.a.byOrganization[org.id], visibleResults.b.byOrganization[org.id], paymentMethod)} />)}
      </section>
    </>}
  </div>;
}
